import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SideNavbar from '../components/Navbar/SideNavbar';
import Header from '../components/Header/Header';
import { apis } from '../../core/services/params';
import axiosInstance from '../../core/services/axiosConfig';
import WhatsAppConversationModal from '../components/Crm/WhatsAppConversationModal';
import {
    Table,
    Input,
    DatePicker,
    Select,
    Button,
    Tag,
    Modal,
    Spin,
    Empty,
    Space,
    Typography,
    Badge,
    Dropdown,
    Alert,
    Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
    SearchOutlined,
    ReloadOutlined,
    WhatsAppOutlined,
    MessageOutlined,
    ClockCircleOutlined,
    EyeOutlined,
    MoreOutlined,
    DownloadOutlined,
    FilterOutlined,
    BarChartOutlined,
    ArrowRightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

/* ─── Typography ─────────────────────────────────────────────── */
const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

/* ─── Interfaces ─────────────────────────────────────────────── */
interface ConversationMeta {
    customer_feedback_values: string[];
    has_customer_feedback: boolean;
    has_order_confirmation: boolean;
    has_order_enquiry: boolean;
    has_product_search: boolean;
    has_raised_issue: boolean;
    has_ticket_enquiry: boolean;
    has_tracked_order: boolean;
    order_confirmation_ids: number[];
    order_enquiry_ids: number[];
    phone: string;
    product_search_queries: string[];
    raised_issue_ticket_ids: number[];
    session_id: string;
    ticket_enquiry_ids: number[];
    tracked_order_item_ids: number[];
}

interface ChatRecord {
    sender_id: string;
    last_interaction: string;
    total_chats: number;
    conversation_meta: ConversationMeta;
}

interface DashboardResponse {
    data: ChatRecord[];
    pagination: {
        total_records: number;
        total_pages: number;
        page: number;
        limit: number;
        has_next: boolean;
        has_previous: boolean;
    };
    filters: {
        sender_id: string | null;
        start_date: string | null;
        end_date: string | null;
    };
    success: boolean;
}

interface ChatMessage {
    role: 'user' | 'bot';
    text: string;
    buttons?: string;
    timestamp?: string;
}

interface ChatHistoryResponse {
    chat_history: ChatMessage[];
    last_interaction: string;
    success: boolean;
}

interface RawConversation {
    date_time: string;
    detected_intent_tag: string | null;
    message: string;
    message_id: string | null;
    phone: string | null;
    session_id: string;
    user_bot: 'user' | 'bot';
}

interface WhatsAppReportResponse {
    date_range: { end_date: string; start_date: string; total_days: number };
    orders: any[];
    raw_conversations: RawConversation[];
    status: string;
    summary: {
        intent_distribution: Record<string, number>;
        leads_submitted_to_telesales: number;
        no_reply_chats: number;
        order_enquiries: number;
        order_metrics: { conversations: number; customer_replies: number; total_messages: number };
        other_enquiries: number;
        sales_enquiries: number;
        sales_metrics: { conversations: number; customer_replies: number; total_messages: number };
        total_conversations: number;
    };
    total_conversations: number;
}


/* ─── Helpers ────────────────────────────────────────────────── */
const formatDateForAPI = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
};

// V1 (whatsapp-chatbot-reports-archive) is retired along with its Mongo host;
// the report generator only writes the conversation prefix now.
const DAILY_REPORT_PREFIX = 'whatsapp-chatbot-reports-conversation';

const buildDailyReportPath = (dateString: string) =>
    `${DAILY_REPORT_PREFIX}/daily/${formatDateForAPI(dateString)}/daily-dump.csv`;

const timeAgo = (dateStr: string) => {
    if (!dateStr) return '';
    const parts = dateStr.split(' ');
    return parts[1] ? parts[1].slice(0, 5) : '';
};

/* ─── CMS theme tokens ──────────────────────────────────── */
const CMS = {
    hero: '#43425d',          // sidebar / primary dark
    heroDark: '#2b2b40',      // deeper shade
    heroLight: '#57567a',     // lighter slate
    accent: '#25D366',        // WhatsApp green (accent only)
    accentDark: '#128C7E',
    border: '#eef0f6',
    bg: '#f4f5f9',
    cardBg: '#ffffff',
    text: '#2d2c3e',
    textMuted: '#8a8aaa',
};

/* ─── Inline styles ──────────────────────────────────────────── */
const styles: Record<string, React.CSSProperties> = {
    page: {
        padding: '20px 24px 32px',
        background: CMS.bg,
        minHeight: '100vh',
        fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
    },
    /* Hero header bar — matches CMS sidebar hue */
    heroBar: {
        background: `linear-gradient(135deg, ${CMS.heroDark} 0%, ${CMS.hero} 60%, ${CMS.heroLight} 100%)`,
        borderRadius: 16,
        padding: '20px 28px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 12px 30px rgba(67,66,93,0.15)',
        border: '1px solid rgba(255,255,255,0.08)',
        position: 'relative',
        overflow: 'hidden',
    },
    heroDecor: {
        position: 'absolute', right: -40, top: -40,
        width: 180, height: 180, borderRadius: '50%',
        background: 'rgba(255,255,255,0.04)',
    },
    heroDecor2: {
        position: 'absolute', right: 90, bottom: -50,
        width: 120, height: 120, borderRadius: '50%',
        background: 'rgba(255,255,255,0.03)',
    },
    heroDecor3: {
        position: 'absolute', left: 220, top: -15,
        width: 70, height: 70, borderRadius: '50%',
        background: 'rgba(37,211,102,0.08)',   /* subtle WA green glow */
    },
    filterCard: {
        background: CMS.cardBg,
        borderRadius: 14,
        border: `1px solid ${CMS.border}`,
        padding: '18px 24px',
        marginBottom: 20,
        boxShadow: '0 10px 30px rgba(67,66,93,0.03), 0 1px 3px rgba(67,66,93,0.02)',
    },
    filterLabel: {
        fontSize: 10,
        fontWeight: 700,
        color: CMS.textMuted,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.06em',
        marginBottom: 6,
        display: 'block',
    },
    tableCard: {
        background: CMS.cardBg,
        borderRadius: 14,
        border: `1px solid ${CMS.border}`,
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(67,66,93,0.03)',
    },
    /* Quick range chips */
    quickChip: {
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 16,
        padding: '5px 12px',
        fontSize: 11,
        fontWeight: 600,
        color: '#64748b',
        cursor: 'pointer',
        height: 28,
        lineHeight: '16px',
        transition: 'all 0.18s ease',
        whiteSpace: 'nowrap' as const,
    },
    quickChipActive: {
        background: 'linear-gradient(135deg, #43425d 0%, #2b2b40 100%)',
        borderColor: '#43425d',
        color: '#fff',
        boxShadow: '0 3px 10px rgba(67,66,93,0.18)',
    },
    /* Summary stat cards */
    summaryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        marginBottom: 10,
    },
    summaryCard: {
        background: CMS.cardBg,
        borderRadius: 12,
        border: `1px solid ${CMS.border}`,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        boxShadow: '0 6px 18px rgba(67,66,93,0.03), 0 1px 3px rgba(67,66,93,0.02)',
    },
    summaryCardHero: {
        background: `linear-gradient(135deg, ${CMS.heroDark} 0%, ${CMS.hero} 70%, ${CMS.heroLight} 100%)`,
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 8px 20px rgba(67,66,93,0.18)',
    },
    summaryIconWrap: {
        width: 34, height: 34, borderRadius: 10,
        background: 'rgba(255,255,255,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
    },
    summaryLabel: {
        fontSize: 10, fontWeight: 700, color: CMS.textMuted,
        textTransform: 'uppercase' as const, letterSpacing: '0.04em',
        marginBottom: 1,
    },
    summaryLabelLight: {
        fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.7)',
        textTransform: 'uppercase' as const, letterSpacing: '0.04em',
        marginBottom: 1,
    },
    summaryValue: {
        fontSize: 19, fontWeight: 800, lineHeight: 1.1, color: CMS.text,
    },
    summaryValueLight: {
        fontSize: 20, fontWeight: 800, lineHeight: 1.1, color: '#fff',
    },
    summarySub: {
        fontSize: 10, fontWeight: 500, marginTop: 2,
    },
    summaryStrip: {
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        background: CMS.cardBg,
        borderRadius: 12,
        border: `1px solid ${CMS.border}`,
        padding: '10px 18px',
        marginBottom: 20,
        fontSize: 12,
        color: CMS.textMuted,
        boxShadow: '0 4px 14px rgba(67,66,93,0.02)',
    },
    summaryStripItem: {
        fontSize: 12, fontWeight: 600, color: CMS.textMuted,
    },
    summaryStripDivider: {
        width: 1, height: 14, background: CMS.border, display: 'inline-block',
    },
    phoneChip: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: 'linear-gradient(135deg, #f0fdf4 0%, #e6fbf0 100%)',
        border: '1px solid #bbf7d0',
        borderRadius: 20, padding: '4px 10px 4px 8px',
        boxShadow: '0 1px 3px rgba(37,211,102,0.05)',
        transition: 'all 0.2s ease',
    },
    tagBlue: {
        background: '#eff6ff',
        color: '#2563eb',
        border: '1px solid #dbeafe',
        borderRadius: 6,
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
    },
    tagRed: {
        background: '#fef2f2',
        color: '#dc2626',
        border: '1px solid #fee2e2',
        borderRadius: 6,
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
    },
    tagGreen: {
        background: '#f0fdf4',
        color: '#16a34a',
        border: '1px solid #dcfce7',
        borderRadius: 6,
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
    },
    tagPurple: {
        background: '#f5f3ff',
        color: '#7c3aed',
        border: '1px solid #e0e7ff',
        borderRadius: 6,
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
    },
    tagOrange: {
        background: '#fff7ed',
        color: '#ea580c',
        border: '1px solid #ffedd5',
        borderRadius: 6,
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
    },
    tagYellow: {
        background: '#fffbeb',
        color: '#d97706',
        border: '1px solid #fef3c7',
        borderRadius: 6,
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
    },
};

/* ─── Main Component ─────────────────────────────────────────── */
const WhatsAppChatHistoryDashboard: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    /* data */
    const [records, setRecords] = useState<ChatRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    /* pagination */
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    /* filters */
    const today = new Date().toISOString().split('T')[0];
    const [senderIdFilter, setSenderIdFilter] = useState('');
    const [dateRange, setDateRange] = useState<[string, string]>([today, today]);
    const [eventTypeFilter, setEventTypeFilter] = useState(searchParams.get('event_type') || '');

    /* conversation modal */
    const [selectedRecord, setSelectedRecord] = useState<ChatRecord | null>(null);
    const [conversationModal, setConversationModal] = useState(false);

    /* daily report modal */
    const [dailyReportModal, setDailyReportModal] = useState(false);
    const [dailyReportDate, setDailyReportDate] = useState<string>(dayjs().subtract(1, 'day').format('YYYY-MM-DD'));
    const [downloadingDailyReport, setDownloadingDailyReport] = useState(false);
    const [dailyReportError, setDailyReportError] = useState('');

    /* return exchange report modal */
    const [returnExchangeModal, setReturnExchangeModal] = useState(false);
    const [returnExchangeDate, setReturnExchangeDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
    const [downloadingReturnExchange, setDownloadingReturnExchange] = useState(false);
    const [returnExchangeError, setReturnExchangeError] = useState('');

    /* ── API calls ─────────────────────────────────────────────── */
    const fetchDashboardData = async (page = currentPage, pageLimit = limit, range: [string, string] = dateRange) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: page.toString(), limit: pageLimit.toString() });
            if (senderIdFilter.trim()) params.append('sender_id', '91' + senderIdFilter.trim());
            if (range[0]) params.append('start_date', formatDateForAPI(range[0]));
            if (range[1]) params.append('end_date', formatDateForAPI(range[1]));
            if (eventTypeFilter) params.append('event_type', eventTypeFilter);

            const response = await axiosInstance.get<DashboardResponse>(`${apis.FETCH_WHATSAPP_DASHBOARD}?${params.toString()}`);
            if (response.data?.success) {
                setRecords(response.data.data || []);
                setTotalRecords(response.data.pagination.total_records || 0);
            } else {
                setRecords([]);
                setError('No data found for the selected criteria');
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to fetch dashboard data');
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchConversation = (record: ChatRecord) => {
        setSelectedRecord(record);
        setConversationModal(true);
    };

    const handleDownloadDailyReport = async () => {
        if (!dailyReportDate) return;
        setDownloadingDailyReport(true); setDailyReportError('');
        try {
            const filePath = buildDailyReportPath(dailyReportDate);
            const response = await axiosInstance.post<{ download_url: string }>(apis.DOWNLOAD_WHATSAPP_DAILY_REPORT, { file_path: filePath });
            if (response.data?.download_url) {
                const a = document.createElement('a');
                a.href = response.data.download_url;
                a.setAttribute('download', `whatsapp_daily_${dailyReportDate}.csv`);
                a.style.display = 'none';
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                setDailyReportModal(false);
            } else {
                setDailyReportError('Failed to get download URL.');
            }
        } catch (err: any) {
            setDailyReportError(err?.response?.data?.message || 'Report not available for selected date.');
        } finally { setDownloadingDailyReport(false); }
    };

    const handleDownloadReturnExchange = async () => {
        if (!returnExchangeDate) return;
        setDownloadingReturnExchange(true);
        setReturnExchangeError('');
        try {
            const [y, m, d] = returnExchangeDate.split('-');
            const formattedDate = `${d}-${m}-${y}`;
            const response = await axiosInstance.get<{ status: boolean; count: number; data: any[] }>(
                `${apis.DOWNLOAD_RETURN_EXCHANGE_REPORT}?date=${formattedDate}`
            );

            if (response.data && response.data.status && Array.isArray(response.data.data)) {
                const csvData = response.data.data;
                if (csvData.length === 0) {
                    setReturnExchangeError('No return/exchange requests found for the selected date.');
                    return;
                }

                // Define CSV headers exactly as requested
                const headers = [
                    '_id',
                    'phone',
                    'order_id',
                    'item_id',
                    'request_type',
                    'payment_type',
                    'quantity',
                    'bank_name',
                    'reason_key',
                    'account_name',
                    'account_number',
                    'ifsc_code',
                    'comment',
                    'reason',
                    'product_msn',
                    'product_name',
                    'customer_id',
                    'status',
                    'active',
                    'scheduler_count',
                    'created_at',
                    'updated_at',
                    'datetime_ms',
                    'user_uploaded_documents',
                    'document_dict',
                    'online_response',
                    'online_status',
                    'requestStatus'
                ];

                // Helper to escape special characters for CSV
                const escapeCSV = (val: any) => {
                    if (val === null || val === undefined) return '';
                    let str = String(val);
                    str = str.replace(/"/g, '""');
                    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
                        return `"${str}"`;
                    }
                    return str;
                };

                // Build row contents with exact keys mapping
                const rows = csvData.map((item: any) => {
                    const formatField = (fieldVal: any) => {
                        if (fieldVal === null || fieldVal === undefined) return '';
                        if (typeof fieldVal === 'object') return JSON.stringify(fieldVal);
                        return fieldVal;
                    };
                    return [
                        item._id,
                        item.phone,
                        item.order_id,
                        item.item_id,
                        item.request_type,
                        item.payment_type,
                        item.quantity,
                        item.bank_name,
                        item.reason_key,
                        item.account_name,
                        item.account_number,
                        item.ifsc_code,
                        item.comment,
                        item.reason,
                        item.product_msn,
                        item.product_name,
                        item.customer_id,
                        item.status,
                        item.active,
                        item.scheduler_count,
                        item.created_at,
                        item.updated_at,
                        item.datetime_ms,
                        formatField(item.user_uploaded_documents),
                        formatField(item.document_dict),
                        formatField(item.online_response),
                        item.online_status,
                        item.requestStatus
                    ].map(escapeCSV).join(',');
                });

                const csvContent = [headers.join(','), ...rows].join('\n');

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.setAttribute('download', `return_exchange_report_${formattedDate}.csv`);
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                setReturnExchangeModal(false);
            } else {
                setReturnExchangeError('Invalid response format or failed status from server.');
            }
        } catch (err: any) {
            setReturnExchangeError(err?.response?.data?.message || 'Report not available or failed to fetch.');
        } finally {
            setDownloadingReturnExchange(false);
        }
    };

    const handleReset = () => {
        setSenderIdFilter(''); setDateRange([today, today]); setEventTypeFilter('');
        setCurrentPage(1); fetchDashboardData(1, limit);
    };

    const handleSearch = () => {
        setCurrentPage(1); fetchDashboardData(1, limit);
    };

    /* quick date-range presets */
    const quickRanges: { label: string; days: number; offset?: number }[] = [
        { label: 'Today', days: 0 },
        { label: 'Yesterday', days: 0, offset: 1 },
        { label: 'Last 3 Days', days: 2 },
        { label: 'Last 7 Days', days: 6 },
        { label: 'Last 30 Days', days: 29 },
        { label: 'Last 90 Days', days: 89 },
    ];

    const isRangeActive = (days: number, offset = 0) => {
        const end = dayjs().subtract(offset, 'day').format('YYYY-MM-DD');
        const start = dayjs().subtract(offset + days, 'day').format('YYYY-MM-DD');
        return dateRange[0] === start && dateRange[1] === end;
    };

    const applyQuickRange = (days: number, offset = 0) => {
        const end = dayjs().subtract(offset, 'day').format('YYYY-MM-DD');
        const start = dayjs().subtract(offset + days, 'day').format('YYYY-MM-DD');
        const range: [string, string] = [start, end];
        setDateRange(range);
        setCurrentPage(1);
        fetchDashboardData(1, limit, range);
    };

    useEffect(() => { fetchDashboardData(); }, [currentPage, limit]);

    /* ── Row actions ───────────────────────────────────────────── */
    const getRowMenuItems = (record: ChatRecord): MenuProps['items'] => [
        {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'View Conversation',
            onClick: () => fetchConversation(record),
        },
    ];

    /* ── Table columns ─────────────────────────────────────────── */
    const columns: ColumnsType<ChatRecord> = [
        {
            title: 'Contact',
            key: 'phone',
            width: 175,
            render: (_: any, r: ChatRecord) => (
                <div style={styles.phoneChip}>
                    <WhatsAppOutlined style={{ color: '#25D366', fontSize: 15 }} />
                    <Text strong style={{ fontSize: 13, color: '#166534' }}>+{r.conversation_meta.phone}</Text>
                </div>
            ),
        },
        {
            title: 'Messages',
            dataIndex: 'total_chats',
            key: 'total_chats',
            width: 90,
            align: 'center',
            render: (count: number) => (
                <Badge
                    count={count}
                    style={{ background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 12, minWidth: 28 }}
                />
            ),
        },
        {
            title: 'Last Active',
            dataIndex: 'last_interaction',
            key: 'last_interaction',
            width: 95,
            align: 'center',
            render: (d: string) => (
                <span style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    <ClockCircleOutlined style={{ fontSize: 11 }} />{timeAgo(d) || 'N/A'}
                </span>
            ),
        },
        {
            title: 'Order Enquiry',
            key: 'oe',
            width: 160,
            render: (_: any, r: ChatRecord) => {
                const ids = r.conversation_meta.order_enquiry_ids;
                return ids?.length
                    ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{Array.from(new Set(ids)).map(id => <span key={id} style={styles.tagBlue}>#{id}</span>)}</div>
                    : <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>;
            },
        },
        {
            title: 'Issue Raised',
            key: 'ir',
            width: 160,
            render: (_: any, r: ChatRecord) => {
                const ids = r.conversation_meta.raised_issue_ticket_ids;
                return ids?.length
                    ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{Array.from(new Set(ids)).map(id => <span key={id} style={styles.tagRed}>#{id}</span>)}</div>
                    : <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>;
            },
        },
        {
            title: 'Order Confirmed',
            key: 'oc',
            width: 160,
            render: (_: any, r: ChatRecord) => {
                const ids = r.conversation_meta.order_confirmation_ids;
                return ids?.length
                    ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{Array.from(new Set(ids)).map(id => <span key={id} style={styles.tagGreen}>#{id}</span>)}</div>
                    : <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>;
            },
        },
        {
            title: 'Tracked Order',
            key: 'to',
            width: 150,
            render: (_: any, r: ChatRecord) => {
                const ids = r.conversation_meta.tracked_order_item_ids;
                return ids?.length
                    ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{Array.from(new Set(ids)).map(id => <span key={id} style={styles.tagPurple}>#{id}</span>)}</div>
                    : <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>;
            },
        },
        {
            title: 'Product Search',
            key: 'ps',
            width: 200,
            render: (_: any, r: ChatRecord) => {
                const q = r.conversation_meta.product_search_queries;
                if (!q?.length) return <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>;
                return (
                    <Tooltip title={q.join(', ')}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 195 }}>
                            {q.slice(0, 2).map((s, i) => (
                                <span key={i} style={{ ...styles.tagOrange, maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s}</span>
                            ))}
                            {q.length > 2 && <span style={{ ...styles.tagOrange, padding: '2px 5px' }}>+{q.length - 2}</span>}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Feedback',
            key: 'fb',
            width: 130,
            render: (_: any, r: ChatRecord) => {
                const vals = r.conversation_meta.customer_feedback_values;
                if (!vals?.length) return <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>;
                const styleMap: Record<string, React.CSSProperties> = {
                    GOOD: styles.tagGreen,
                    POOR: styles.tagRed,
                    AVERAGE: styles.tagYellow
                };
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {vals.map((v, i) => <span key={i} style={styleMap[v] || styles.tagBlue}>{v}</span>)}
                    </div>
                );
            },
        },
        {
            title: '',
            key: 'actions',
            align: 'center',
            width: 52,
            render: (_val, record) => (
                <Dropdown menu={{ items: getRowMenuItems(record) }} trigger={['click']} placement="bottomRight">
                    <Button
                        type="text"
                        icon={<MoreOutlined style={{ fontSize: 17 }} />}
                        style={{ color: '#9ca3af', borderRadius: 8 }}
                        onClick={e => e.stopPropagation()}
                    />
                </Dropdown>
            ),
        },
    ];


    /* ── Render ────────────────────────────────────────────────── */

    return (
        <div className="flex mainWrapper w-full">
            <div className="sidenav-wrapper"><SideNavbar /></div>
            <div className="right-container">
                <div className="header-container"><Header /></div>
                <div className="body-container">
                    <div style={styles.page}>

                        {/* ── Hero bar ───────────────────────────────────────── */}
                        <div style={styles.heroBar as React.CSSProperties}>
                            <div style={styles.heroDecor as React.CSSProperties} />
                            <div style={styles.heroDecor2 as React.CSSProperties} />
                            <div style={styles.heroDecor3 as React.CSSProperties} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'relative', zIndex: 1 }}>
                                <div style={{
                                    width: 48, height: 48, borderRadius: 14,
                                    background: CMS.accent,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                    boxShadow: '0 3px 12px rgba(37,211,102,0.35)',
                                }}>
                                    <WhatsAppOutlined style={{ color: '#fff', fontSize: 24 }} />
                                </div>
                                <div>
                                    <h1 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px' }}>
                                        WhatsApp Chat Dashboard
                                    </h1>
                                    <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 400 }}>
                                        Monitor &amp; review all WhatsApp chatbot conversations (whatsapp_chatbot_service)
                                    </p>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative', zIndex: 1 }}>
                                <Button
                                    icon={<BarChartOutlined />}
                                    onClick={() => {
                                        const params = new URLSearchParams({
                                            start_date: dateRange[0],
                                            end_date: dateRange[1],
                                        });
                                        navigate(`/whatsapp-chatbot-analytics/detailed?${params.toString()}`);
                                    }}
                                    style={{
                                        background: '#ffffff',
                                        border: '1px solid #ffffff',
                                        color: CMS.hero,
                                        borderRadius: 8,
                                        fontWeight: 700,
                                        height: 36,
                                        fontSize: 13,
                                        boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
                                    }}
                                    className="detailed-analytics-btn"
                                >
                                    Detailed Analytics <ArrowRightOutlined style={{ fontSize: 11 }} />
                                </Button>
                                <Button
                                    icon={<DownloadOutlined />}
                                    onClick={() => { setDailyReportError(''); setDailyReportModal(true); }}
                                    style={{
                                        background: 'rgba(255,255,255,0.08)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        color: '#fff', borderRadius: 8, fontWeight: 600,
                                        backdropFilter: 'blur(10px)',
                                        height: 36,
                                        fontSize: 13,
                                    }}
                                    className="hero-btn"
                                >
                                    Daily Report
                                </Button>
                                <Button
                                    icon={<DownloadOutlined />}
                                    onClick={() => { setReturnExchangeError(''); setReturnExchangeModal(true); }}
                                    style={{
                                        background: 'rgba(255,255,255,0.08)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        color: '#fff', borderRadius: 8, fontWeight: 600,
                                        backdropFilter: 'blur(10px)',
                                        height: 36,
                                        fontSize: 13,
                                    }}
                                    className="hero-btn"
                                >
                                    Return Exchange Report
                                </Button>
                            </div>
                        </div>

                        {/* ── Filter card ────────────────────────────────────── */}
                        <div style={styles.filterCard}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
                                <FilterOutlined style={{ color: CMS.hero, fontSize: 13 }} />
                                <span style={{ fontWeight: 700, fontSize: 13, color: CMS.text }}>Filters &amp; Search</span>
                            </div>
                            <Space size={16} wrap align="end">
                                <div>
                                    <span style={styles.filterLabel}>Sender ID</span>
                                    <Input
                                        prefix={<span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>+91</span>}
                                        placeholder="Phone number…"
                                        value={senderIdFilter}
                                        onChange={e => setSenderIdFilter(e.target.value)}
                                        onPressEnter={handleSearch}
                                        style={{ width: 200, borderRadius: 8, height: 36 }}
                                    />
                                </div>
                                <div>
                                    <span style={styles.filterLabel}>Date Range</span>
                                    <RangePicker
                                        value={[dayjs(dateRange[0], 'YYYY-MM-DD'), dayjs(dateRange[1], 'YYYY-MM-DD')]}
                                        onChange={vals => {
                                            if (vals && vals[0] && vals[1])
                                                setDateRange([vals[0].format('YYYY-MM-DD'), vals[1].format('YYYY-MM-DD')]);
                                        }}
                                        disabledDate={d => d && d.isAfter(dayjs(), 'day')}
                                        format="DD/MM/YYYY"
                                        allowClear={false}
                                        style={{ borderRadius: 8, height: 36 }}
                                    />
                                </div>
                                {/* ── Quick date-range chips ─────────────────── */}
                                <div>
                                    <span style={styles.filterLabel}>Quick Range</span>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {quickRanges.map(({ label, days, offset }) => {
                                            const active = isRangeActive(days, offset);
                                            return (
                                                <button
                                                    key={label}
                                                    type="button"
                                                    onClick={() => applyQuickRange(days, offset)}
                                                    style={{
                                                        ...styles.quickChip,
                                                        ...(active ? styles.quickChipActive : {}),
                                                    }}
                                                    className={active ? 'quick-chip quick-chip-active' : 'quick-chip'}
                                                >
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div>
                                    <span style={styles.filterLabel}>Event Type</span>
                                    <Select
                                        value={eventTypeFilter || undefined}
                                        placeholder="All Events"
                                        onChange={val => setEventTypeFilter(val || '')}
                                        allowClear
                                        style={{ width: 185 }}
                                    >
                                        <Option value="">All Events</Option>
                                        <Option value="FEEDBACK_POOR">Feedback Poor</Option>
                                        <Option value="FEEDBACK_GOOD">Feedback Good</Option>
                                        <Option value="FEEDBACK_AVERAGE">Feedback Average</Option>
                                        <Option value="BOT_ASKED_REPHRASE">Bot Asked Rephrase</Option>
                                        <Option value="CHAT_RESTARTED">Chat Restarted</Option>
                                    </Select>
                                </div>
                                <Space size={8}>
                                    <Button
                                        type="primary"
                                        icon={<SearchOutlined />}
                                        loading={loading}
                                        onClick={handleSearch}
                                        style={{
                                            borderRadius: 8,
                                            background: 'linear-gradient(135deg, #43425d 0%, #2b2b40 100%)',
                                            borderColor: '#43425d',
                                            fontWeight: 600,
                                            height: 36,
                                            boxShadow: '0 4px 12px rgba(67, 66, 93, 0.15)'
                                        }}
                                        className="search-btn"
                                    >
                                        Search
                                    </Button>
                                    <Tooltip title="Reset filters">
                                        <Button
                                            icon={<ReloadOutlined />}
                                            onClick={handleReset}
                                            style={{ borderRadius: 8, height: 36, border: '1px solid #e2e8f0', color: '#64748b' }}
                                        />
                                    </Tooltip>
                                </Space>
                            </Space>
                        </div>

                        {/* ── Error alert ────────────────────────────────────── */}
                        {error && (
                            <Alert
                                type="error"
                                message={error}
                                closable
                                onClose={() => setError('')}
                                style={{ marginBottom: 16, borderRadius: 10 }}
                            />
                        )}

                        {/* ── Conversations Table Card ──────────────────────────── */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                            <MessageOutlined style={{ color: CMS.hero, fontSize: 15 }} />
                            <span style={{ fontWeight: 700, fontSize: 15, color: CMS.text, letterSpacing: '-0.1px' }}>All Conversations</span>
                            {totalRecords > 0 && (
                                <span style={{
                                    background: '#eeedf5',
                                    color: CMS.hero, fontSize: 11, fontWeight: 700,
                                    padding: '2px 10px', borderRadius: 20,
                                    border: `1px solid #c8c7df`,
                                }}>
                                    {totalRecords.toLocaleString()} total
                                </span>
                            )}
                        </div>
                        <div style={styles.tableCard}>
                            <Table<ChatRecord>
                                rowKey="sender_id"
                                columns={columns}
                                dataSource={records}
                                loading={loading ? { indicator: <Spin size="large" /> } : false}
                                locale={{
                                    emptyText: (
                                        <Empty
                                            image={<WhatsAppOutlined style={{ fontSize: 48, color: '#25D366', opacity: 0.4 }} />}
                                            description={<span style={{ color: '#9ca3af' }}>No conversations found for the selected filters</span>}
                                            style={{ padding: '48px 0' }}
                                        />
                                    ),
                                }}
                                pagination={{
                                    current: currentPage,
                                    pageSize: limit,
                                    total: totalRecords,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    pageSizeOptions: ['5', '10', '25', '50', '100'],
                                    showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total}`,
                                    onChange: (page, pageSize) => { setCurrentPage(page); setLimit(pageSize ?? limit); },
                                    style: { padding: '14px 20px' },
                                }}
                                onRow={record => ({
                                    onClick: () => fetchConversation(record),
                                    style: { cursor: 'pointer' },
                                })}
                                rowClassName={() => 'wa-table-row'}
                                size="middle"
                            />
                        </div>

                    </div>
                </div>
            </div>

            {/* ── Conversation Modal (CRM component) ──────────────── */}
            <WhatsAppConversationModal
                visible={conversationModal}
                phone={selectedRecord?.conversation_meta.phone ?? undefined}
                onClose={() => { setConversationModal(false); setSelectedRecord(null); }}
                chatHistoryEndpoint={apis.FETCH_WHATSAPP_CHAT_HISTORY}
            />

            {/* ═══════════════════════════════════════════════════════
                  DAILY REPORT DOWNLOAD MODAL
              ═══════════════════════════════════════════════════════ */}
            <Modal
                open={dailyReportModal}
                onCancel={() => { setDailyReportModal(false); setDailyReportError(''); }}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <DownloadOutlined style={{ color: '#25D366', fontSize: 18 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 15 }}>Download Daily Report</div>
                            <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>WhatsApp chatbot archive</div>
                        </div>
                    </div>
                }
                footer={[
                    <Button key="cancel" onClick={() => { setDailyReportModal(false); setDailyReportError(''); }} style={{ borderRadius: 10 }}>
                        Cancel
                    </Button>,
                    <Button
                        key="download"
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={downloadingDailyReport}
                        onClick={handleDownloadDailyReport}
                        style={{ background: '#00a884', borderColor: '#00a884', borderRadius: 10, fontWeight: 600 }}
                    >
                        Download CSV
                    </Button>,
                ]}
                width={440}
                styles={{ body: { borderRadius: 16 } }}
            >
                <div style={{ padding: '8px 0' }}>
                    <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>
                        Select a date to download the daily archive report (CSV).
                    </Text>
                    <div>
                        <span style={{ ...styles.filterLabel, marginBottom: 6 }}>Select Date</span>
                        <DatePicker
                            value={dayjs(dailyReportDate, 'YYYY-MM-DD')}
                            onChange={val => { if (val) setDailyReportDate(val.format('YYYY-MM-DD')); }}
                            disabledDate={d => d && (d.isAfter(dayjs().subtract(1, 'day'), 'day') || d.isBefore(dayjs().subtract(90, 'day'), 'day'))}
                            format="DD/MM/YYYY"
                            allowClear={false}
                            style={{ width: '100%', borderRadius: 10 }}
                            size="large"
                        />
                    </div>
                    {dailyReportDate && (
                        <div style={{ marginTop: 10, padding: '8px 12px', background: '#f9fafb', borderRadius: 8, fontSize: 11, color: '#9ca3af', wordBreak: 'break-all' }}>
                            📁 {buildDailyReportPath(dailyReportDate)}
                        </div>
                    )}
                    {dailyReportError && (
                        <Alert type="error" message={dailyReportError} style={{ marginTop: 12, borderRadius: 10 }} showIcon />
                    )}
                </div>
            </Modal>

            {/* ═══════════════════════════════════════════════════════
                  RETURN EXCHANGE REPORT DOWNLOAD MODAL
              ═══════════════════════════════════════════════════════ */}
            <Modal
                open={returnExchangeModal}
                onCancel={() => { setReturnExchangeModal(false); setReturnExchangeError(''); }}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <DownloadOutlined style={{ color: '#25D366', fontSize: 18 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 15 }}>Download Return &amp; Exchange Report</div>
                            <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>Return/Exchange chatbot request archive</div>
                        </div>
                    </div>
                }
                footer={[
                    <Button key="cancel" onClick={() => { setReturnExchangeModal(false); setReturnExchangeError(''); }} style={{ borderRadius: 10 }}>
                        Cancel
                    </Button>,
                    <Button
                        key="download"
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={downloadingReturnExchange}
                        onClick={handleDownloadReturnExchange}
                        style={{ background: '#00a884', borderColor: '#00a884', borderRadius: 10, fontWeight: 600 }}
                    >
                        Download CSV
                    </Button>,
                ]}
                width={440}
                styles={{ body: { borderRadius: 16 } }}
            >
                <div style={{ padding: '8px 0' }}>
                    <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>
                        Select a date to download the return &amp; exchange requests report (CSV).
                    </Text>
                    <div>
                        <span style={{ ...styles.filterLabel, marginBottom: 6 }}>Select Date</span>
                        <DatePicker
                            value={dayjs(returnExchangeDate, 'YYYY-MM-DD')}
                            onChange={val => { if (val) setReturnExchangeDate(val.format('YYYY-MM-DD')); }}
                            disabledDate={d => d && (d.isAfter(dayjs(), 'day') || d.isBefore(dayjs().subtract(90, 'day'), 'day'))}
                            format="DD/MM/YYYY"
                            allowClear={false}
                            style={{ width: '100%', borderRadius: 10 }}
                            size="large"
                        />
                    </div>
                    {returnExchangeError && (
                        <Alert type="error" message={returnExchangeError} style={{ marginTop: 12, borderRadius: 10 }} showIcon />
                    )}
                </div>
            </Modal>

            {/* ── Global table row hover style ───────────────────── */}
            {/* ── Global CSS Overrides for Premium Enterprise Feel ───────────────────── */}
            <style>{`

                /* Table Headers */
                .ant-table-thead > tr > th {
                    background: #f8fafc !important;
                    font-weight: 700 !important;
                    font-size: 11px !important;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    color: #64748b !important;
                    border-bottom: 1px solid #e2e8f0 !important;
                    padding: 14px 16px !important;
                }

                /* Table Rows */
                .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #f1f5f9 !important;
                    padding: 14px 16px !important;
                    font-size: 13px !important;
                    color: #334155 !important;
                }

                .wa-table-row {
                    transition: all 0.2s ease;
                }

                .wa-table-row:hover > td {
                    background: #f8fafc !important;
                }

                .wa-table-row:hover > td:first-child {
                    position: relative;
                }

                .wa-table-row:hover > td:first-child::before {
                    content: "";
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: 0;
                    width: 3px;
                    background: #43425d;
                    border-radius: 0 4px 4px 0;
                }

                /* Custom Ant Form Element Overrides */
                .ant-input-affix-wrapper, .ant-picker {
                    border-radius: 8px !important;
                    border: 1px solid #e2e8f0 !important;
                    box-shadow: none !important;
                    transition: all 0.2s ease !important;
                }

                .ant-select-selector {
                    border-radius: 8px !important;
                    border: 1px solid #e2e8f0 !important;
                    box-shadow: none !important;
                    height: 36px !important;
                    display: flex !important;
                    align-items: center !important;
                    transition: all 0.2s ease !important;
                }

                .ant-input-affix-wrapper:hover, .ant-picker:hover, .ant-select-selector:hover {
                    border-color: #43425d !important;
                }

                .ant-input-affix-wrapper-focused, .ant-picker-focused, .ant-select-focused .ant-select-selector {
                    border-color: #43425d !important;
                    box-shadow: 0 0 0 2px rgba(67, 66, 93, 0.12) !important;
                }

                /* Pagination Custom styling */
                .ant-pagination-item-active {
                    border-color: #43425d !important;
                    background: #43425d !important;
                }
                .ant-pagination-item-active a {
                    color: #ffffff !important;
                }
                .ant-pagination-item:hover, .ant-pagination-next:hover, .ant-pagination-prev:hover {
                    border-color: #43425d !important;
                }
                .ant-pagination-item:hover a, .ant-pagination-next:hover a, .ant-pagination-prev:hover a {
                    color: #43425d !important;
                }

                /* Button scale and transitions */
                .hero-btn, .search-btn {
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
                }
                .hero-btn:hover {
                    transform: translateY(-1px);
                    background: rgba(255, 255, 255, 0.15) !important;
                    border-color: rgba(255, 255, 255, 0.35) !important;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }
                .search-btn:hover {
                    transform: translateY(-1px);
                    opacity: 0.95;
                    box-shadow: 0 6px 16px rgba(67, 66, 93, 0.22) !important;
                }
                .detailed-analytics-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 18px rgba(0,0,0,0.22) !important;
                    background: #f8fafc !important;
                }

                /* Quick range chip hover */
                .quick-chip:not(.quick-chip-active):hover {
                    border-color: #43425d !important;
                    color: #43425d !important;
                    background: #eeedf5 !important;
                }
                .quick-chip-active:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 5px 14px rgba(67,66,93,0.25) !important;
                }
            `}</style>
        </div>
    );
};

export default WhatsAppChatHistoryDashboard;